import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth.service';
import { Trash2, Edit2, Plus, Filter } from 'lucide-react';

interface Species {
  id: number;
  name: string;
}

interface Breed {
  id: number;
  name: string;
  speciesId: number;
  species: Species;
  _count: {
    posts: number;
  };
}

export default function BreedsManagement() {
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBreed, setEditingBreed] = useState<Breed | null>(null);
  const [name, setName] = useState('');
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number | ''>('');
  const [filterSpeciesId, setFilterSpeciesId] = useState<number | ''>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  const loadSpecies = useCallback(async () => {
    try {
      const response = await authService.makeAuthenticatedRequest('/animal-species/species');
      if (response.ok) {
        const data = await response.json();
        setSpecies(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      console.error('Erro ao carregar espécies:', err);
    }
  }, []);

  const loadBreeds = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = filterSpeciesId
        ? `/animal-species/breeds?speciesId=${filterSpeciesId}`
        : '/animal-species/breeds';
      const response = await authService.makeAuthenticatedRequest(url);
      if (response.ok) {
        const data = await response.json();
        setBreeds(Array.isArray(data) ? data : []);
      } else {
        setError('Erro ao carregar raças');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar raças');
    } finally {
      setLoading(false);
    }
  }, [filterSpeciesId]);

  useEffect(() => {
    loadSpecies();
    loadBreeds();
  }, [loadSpecies, loadBreeds]);

  useEffect(() => {
    loadBreeds();
  }, [filterSpeciesId, loadBreeds]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (!selectedSpeciesId) {
      setError('Espécie é obrigatória');
      return;
    }

    setSaveLoading(true);
    setError('');

    try {
      const sanitizedName = name.trim();

      if (editingBreed) {
        const response = await authService.makeAuthenticatedRequest(
          `/animal-species/breeds/${editingBreed.id}`,
          {
            method: 'PUT',
            body: JSON.stringify({ name: sanitizedName, speciesId: Number(selectedSpeciesId) }),
          }
        );

        if (response.ok) {
          await loadBreeds();
          setSuccess('Raça atualizada com sucesso!');
          closeModal();
          setTimeout(() => setSuccess(''), 5000);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Erro ao atualizar raça');
        }
      } else {
        const response = await authService.makeAuthenticatedRequest(
          '/animal-species/breeds',
          {
            method: 'POST',
            body: JSON.stringify({ name: sanitizedName, speciesId: Number(selectedSpeciesId) }),
          }
        );

        if (response.ok) {
          await loadBreeds();
          setSuccess('Raça criada com sucesso!');
          closeModal();
          setTimeout(() => setSuccess(''), 5000);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Erro ao criar raça');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar raça');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id: number, breedName: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir a raça "${breedName}"?`)) {
      return;
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `/animal-species/breeds/${id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        await loadBreeds();
        setSuccess('Raça excluída com sucesso!');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Erro ao excluir raça');
        setTimeout(() => setError(''), 8000);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir raça');
      setTimeout(() => setError(''), 8000);
    }
  };

  const openModalToAdd = () => {
    setEditingBreed(null);
    setName('');
    setSelectedSpeciesId('');
    setError('');
    setShowModal(true);
  };

  const openModalToEdit = (breed: Breed) => {
    setEditingBreed(breed);
    setName(breed.name);
    setSelectedSpeciesId(breed.speciesId);
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBreed(null);
    setName('');
    setSelectedSpeciesId('');
    setError('');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--card-foreground)' }}>
          Raças Cadastradas
        </h2>
        <button
          onClick={openModalToAdd}
          style={{
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
            color: 'white',
            fontWeight: 600,
            padding: '12px 20px',
            border: 'none',
            borderRadius: 10,
            fontSize: 15,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          <Plus size={18} />
          Nova Raça
        </button>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Filter size={20} color="var(--muted-foreground)" />
        <label style={{ fontWeight: 600, color: 'var(--card-foreground)' }}>Filtrar por espécie:</label>
        <select
          value={filterSpeciesId}
          onChange={(e) => setFilterSpeciesId(e.target.value ? Number(e.target.value) : '')}
          style={{
            background: 'var(--input)',
            border: '2px solid var(--border)',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          <option value="">Todas as espécies</option>
          {species.map((sp) => (
            <option key={sp.id} value={sp.id}>
              {sp.name}
            </option>
          ))}
        </select>
      </div>

      {success && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: 16,
          borderRadius: 8,
          marginBottom: 24,
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
        }}>
          {success}
        </div>
      )}

      {error && (
        <div style={{
          background: '#ef4444',
          color: 'white',
          padding: 16,
          borderRadius: 8,
          marginBottom: 24,
          fontWeight: 500,
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted-foreground)' }}>
          Carregando raças...
        </div>
      ) : (
        <div style={{ background: 'var(--card)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--muted)', color: 'var(--card-foreground)' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600 }}>Nome</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600 }}>Espécie</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600 }}>Posts</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {breeds.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'var(--muted-foreground)' }}>
                    {filterSpeciesId ? 'Nenhuma raça cadastrada para esta espécie' : 'Nenhuma raça cadastrada'}
                  </td>
                </tr>
              ) : (
                breeds.map((breed, index) => (
                  <tr
                    key={breed.id}
                    style={{
                      background: index % 2 === 0 ? 'var(--card)' : 'var(--muted)',
                      borderBottom: '1px solid var(--border)',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = index % 2 === 0 ? 'var(--card)' : 'var(--muted)')}
                  >
                    <td style={{ padding: '16px', fontWeight: 600 }}>{breed.name}</td>
                    <td style={{ padding: '16px' }}>{breed.species?.name || 'N/A'}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>{breed._count?.posts || 0}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button
                          onClick={() => openModalToEdit(breed)}
                          style={{
                            background: 'var(--primary)',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 14,
                            fontWeight: 600,
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <Edit2 size={14} />
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(breed.id, breed.name)}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 14,
                            fontWeight: 600,
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <Trash2 size={14} />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: 'var(--card)',
              padding: 32,
              borderRadius: 16,
              width: '100%',
              maxWidth: 500,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, color: 'var(--card-foreground)' }}>
              {editingBreed ? 'Editar Raça' : 'Nova Raça'}
            </h2>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label htmlFor="species" style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: 'var(--card-foreground)' }}>
                  Espécie
                </label>
                <select
                  id="species"
                  value={selectedSpeciesId}
                  onChange={(e) => setSelectedSpeciesId(Number(e.target.value))}
                  style={{
                    width: '100%',
                    background: 'var(--input)',
                    border: '2px solid var(--border)',
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 16,
                    boxSizing: 'border-box',
                  }}
                  required
                >
                  <option value="">Selecione uma espécie</option>
                  {species.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="name" style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: 'var(--card-foreground)' }}>
                  Nome da Raça
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder="Ex: Labrador"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'var(--input)',
                    border: '2px solid var(--border)',
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 16,
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>
              {error && <div style={{ color: '#ef4444', fontSize: 14 }}>{error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    background: 'var(--muted)',
                    color: 'var(--card-foreground)',
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saveLoading}
                  style={{
                    background: saveLoading ? 'var(--muted)' : 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                    color: 'white',
                    fontWeight: 600,
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: 8,
                    cursor: saveLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {saveLoading ? 'Salvando...' : editingBreed ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
